import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowRight, Shirt, Home, Baby, LayoutGrid, PackageOpen, Users, Check } from 'lucide-react';

const ServicesPage = () => {
  const { t, language } = useLanguage();

  const services = [
    {
      id: 'closet',
      icon: Shirt,
      title: t('services.closet.title'),
      description: t('services.closet.description'),
      image: 'https://customer-assets.emergentagent.com/job_galene-spaces/artifacts/zj9gqt5n_IMG_4314.jpeg',
      features: language === 'en' ? [
        'Complete wardrobe assessment',
        'Seasonal rotation system',
        'Color and category organization',
        'Custom storage solutions',
        'Donation coordination',
      ] : [
        'Evaluación completa del guardarropa',
        'Sistema de rotación estacional',
        'Organización por color y categoría',
        'Soluciones de almacenamiento personalizadas',
        'Coordinación de donaciones',
      ],
    },
    {
      id: 'decluttering',
      icon: Home,
      title: t('services.decluttering.title'),
      description: t('services.decluttering.description'),
      image: 'https://customer-assets.emergentagent.com/job_galene-spaces/artifacts/7g1ltr19_IMG_2552.jpeg',
      features: language === 'en' ? [
        'Gentle guided decluttering process',
        'Decision-making support',
        'Item sorting and categorization',
        'Donation and disposal handling',
        'Sustainable organizing systems',
      ] : [
        'Proceso de decluttering guiado',
        'Apoyo en toma de decisiones',
        'Clasificación y categorización',
        'Gestión de donaciones',
        'Sistemas de organización sostenibles',
      ],
    },
    {
      id: 'newborn',
      icon: Baby,
      title: t('services.newborn.title'),
      description: t('services.newborn.description'),
      image: 'https://customer-assets.emergentagent.com/job_galene-spaces/artifacts/6gdrk5y7_IMG_3853.jpeg',
      features: language === 'en' ? [
        'Nursery setup and organization',
        'Baby essentials organization',
        'Changing station setup',
        'Clothing size rotation system',
        'Parent-friendly access design',
      ] : [
        'Configuración y organización del cuarto del bebé',
        'Organización de artículos esenciales',
        'Configuración del cambiador',
        'Sistema de rotación por tallas',
        'Diseño de fácil acceso para padres',
      ],
    },
    {
      id: 'rooms',
      icon: LayoutGrid,
      title: t('services.rooms.title'),
      description: t('services.rooms.description'),
      image: 'https://customer-assets.emergentagent.com/job_galene-spaces/artifacts/yh92ezpv_IMG_4613.jpeg',
      features: language === 'en' ? [
        'Kitchen and pantry organization',
        'Home office setup',
        'Bathroom optimization',
        'Living room decluttering',
        'Garage and storage solutions',
      ] : [
        'Organización de cocina y despensa',
        'Configuración de oficina en casa',
        'Optimización de baños',
        'Decluttering de sala de estar',
        'Soluciones de garaje y almacenamiento',
      ],
    },
    {
      id: 'unpacking',
      icon: PackageOpen,
      title: t('services.unpacking.title'),
      description: t('services.unpacking.description'),
      image: 'https://customer-assets.emergentagent.com/job_galene-spaces/artifacts/frffak6p_IMG_5415.jpeg',
      features: language === 'en' ? [
        'Complete unpacking service',
        'Room-by-room organization from scratch',
        'Kitchen and pantry setup',
        'Closet and wardrobe arrangement',
        'Box removal and recycling coordination',
      ] : [
        'Servicio completo de desembalaje',
        'Organización habitación por habitación desde cero',
        'Configuración de cocina y despensa',
        'Organización de armarios y guardarropas',
        'Coordinación de retiro y reciclaje de cajas',
      ],
    },
    {
      id: 'workshops',
      icon: Users,
      title: t('services.workshops.title'),
      description: t('services.workshops.description'),
      image: 'https://customer-assets.emergentagent.com/job_galene-spaces/artifacts/bb63b1d6_IMG_7552.jpeg',
      features: language === 'en' ? [
        'Create your personal vision board',
        'Group and private sessions available',
        'All materials provided',
        'Guided creative experience',
        'Connect with like-minded people',
      ] : [
        'Crea tu propio tablero de visión',
        'Sesiones grupales y privadas disponibles',
        'Todos los materiales incluidos',
        'Experiencia creativa guiada',
        'Conecta con personas afines',
      ],
    },
  ];

  return (
    <main data-testid="services-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#F2F0E9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <span className="font-accent text-lg text-[#8DA399]">
            {t('services.subtitle')}
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#2C3333] mt-4">
            {t('services.title')}
          </h1>
          <p className="mt-6 font-sans text-lg text-[#5C5C5C] max-w-2xl mx-auto">
            {language === 'en'
              ? 'We offer comprehensive organization services designed to bring lasting serenity to your home. Each service is tailored to your unique needs and lifestyle.'
              : 'Ofrecemos servicios integrales de organización diseñados para traer serenidad duradera a tu hogar. Cada servicio está adaptado a tus necesidades y estilo de vida únicos.'}
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="py-16 md:py-24 bg-[#FAFAF9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {services.map((service, index) => (
            <div
              key={service.id}
              id={service.id}
              data-testid={`service-${service.id}`}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center py-16 md:py-24 ${
                index !== services.length - 1 ? 'border-b border-[#E5E5E0]' : ''
              }`}
            >
              {/* Image - alternating sides */}
              <div
                className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-[400px] md:h-[500px] object-cover transition-transform duration-700 hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-[#2C3333]/5" />
                </div>
              </div>

              {/* Content */}
              <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className="p-4 bg-[#F2F0E9] inline-block mb-6">
                  <service.icon className="w-8 h-8 text-[#8DA399]" />
                </div>
                <h2 className="font-serif text-3xl md:text-4xl text-[#2C3333]">
                  {service.title}
                </h2>
                <p className="mt-6 font-sans text-lg text-[#5C5C5C] leading-relaxed">
                  {service.description}
                </p>

                {/* Features List */}
                <ul className="mt-8 space-y-4">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-start space-x-3">
                      <Check className="w-5 h-5 text-[#8DA399] mt-0.5 flex-shrink-0" />
                      <span className="font-sans text-[#5C5C5C]">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/contact" className="inline-block mt-10">
                  <Button
                    data-testid={`service-cta-${service.id}`}
                    className="bg-[#8DA399] text-white hover:bg-[#7A8F85] rounded-full px-8 py-6 text-lg"
                  >
                    {t('services.requestQuote')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#2C3333]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white">
            {language === 'en'
              ? 'Not Sure Where to Start?'
              : '¿No Sabes Por Dónde Empezar?'}
          </h2>
          <p className="mt-6 font-sans text-lg text-white/70 max-w-2xl mx-auto">
            {language === 'en'
              ? 'Book a free consultation and we\'ll help you identify the best approach for your home and lifestyle.'
              : 'Reserva una consulta gratuita y te ayudaremos a identificar el mejor enfoque para tu hogar y estilo de vida.'}
          </p>
          <div className="mt-10">
            <Link to="/contact">
              <Button
                data-testid="services-bottom-cta"
                className="bg-[#8DA399] text-white hover:bg-[#7A8F85] rounded-full px-10 py-6 text-lg"
              >
                {t('hero.ctaSecondary')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServicesPage;
